import { createGlobalStyle } from 'styled-components';

/**
 * Global CSS reset, base styles, and CSS custom-property injection
 * for the Lumina portal.
 *
 * We inject all the Lumina design tokens as CSS custom properties
 * on :root so that every styled-component can reference them via
 * var(--xxx). The dark-theme overrides come from a [data-theme="dark"]
 * selector embedded in the ThemeProvider's mode state.
 */
const GlobalStyles = createGlobalStyle`
  /* ---------- Theme Tokens (light) ---------- */
  :root {
    --bg-primary: #f8fafc;
    --bg-surface: #ffffff;
    --bg-elevated: #f1f5f9;
    --bg-inset: #e2e8f0;
    --bg-overlay: rgba(15, 23, 42, 0.4);

    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    --text-inverse: #f1f5f9;

    --accent: #2d9a8f;
    --accent-hover: #1a7a70;
    --accent-subtle: #e8f7f6;
    --accent-muted: #d1efed;
    --accent-text: #0f5c55;

    --warning: #f97316;
    --warning-subtle: #fff7ed;
    --success: #22c55e;
    --success-subtle: #f0fdf4;
    --error: #ef4444;
    --error-subtle: #fef2f2;
    --info: #3b82f6;
    --info-subtle: #eff6ff;

    --border: #e5e7eb;
    --border-strong: #d1d5db;
    --ring: rgba(45, 154, 143, 0.4);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.04);
    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.08);
    --shadow-xl: 0 16px 48px rgba(0, 0, 0, 0.12);

    --nav-bg: #ffffff;
    --nav-border: #e5e7eb;
    --sidebar-bg: #ffffff;
    --cmd-bg: #ffffff;
    --input-bg: #ffffff;
    --code-bg: #f1f5f9;
  }

  /* ---------- Dark Theme Overrides ---------- */
  [data-theme="dark"] {
    --bg-primary: #0f172a;
    --bg-surface: #1e293b;
    --bg-elevated: #334155;
    --bg-inset: #1e293b;
    --bg-overlay: rgba(0, 0, 0, 0.6);

    --text-primary: #f1f5f9;
    --text-secondary: #cbd5e1;
    --text-muted: #64748b;
    --text-inverse: #0f172a;

    --accent: #4eb8ad;
    --accent-hover: #2d9a8f;
    --accent-subtle: rgba(45, 154, 143, 0.15);
    --accent-muted: rgba(45, 154, 143, 0.25);
    --accent-text: #a3dfdb;

    --warning: #fb923c;
    --warning-subtle: rgba(249, 115, 22, 0.15);
    --success: #4ade80;
    --success-subtle: rgba(34, 197, 94, 0.15);
    --error: #f87171;
    --error-subtle: rgba(239, 68, 68, 0.15);
    --info: #60a5fa;
    --info-subtle: rgba(59, 130, 246, 0.15);

    --border: #334155;
    --border-strong: #475569;
    --ring: rgba(78, 184, 173, 0.4);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.2);
    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.3);
    --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.4);
    --shadow-xl: 0 16px 48px rgba(0, 0, 0, 0.5);

    --nav-bg: #1e293b;
    --nav-border: #334155;
    --sidebar-bg: #1e293b;
    --cmd-bg: #1e293b;
    --input-bg: #334155;
    --code-bg: #334155;
  }

  /* ---------- Reset & Base ---------- */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scroll-behavior: smooth;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-feature-settings: 'cv11', 'ss01';
    background: var(--bg-primary);
    color: var(--text-secondary);
    line-height: 1.6;
    min-height: 100vh;
    transition: background 0.3s ease, color 0.3s ease;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* ---------- Typography ---------- */
  h1, h2, h3, h4, h5, h6 {
    color: var(--text-primary);
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.3;
  }

  h1 { font-size: 2.25rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.25rem; }
  h4 { font-size: 1.125rem; }
  h5 { font-size: 1rem; }

  p { line-height: 1.7; }

  a {
    color: var(--accent);
    text-decoration: none;
    transition: color 0.2s;
  }
  a:hover { color: var(--accent-hover); }

  button {
    cursor: pointer;
    font-family: inherit;
  }

  input, textarea, select {
    font-family: inherit;
  }

  code, pre {
    font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  }

  /* ---------- Scrollbar ---------- */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--border-strong);
  }

  /* ---------- Transitions ---------- */
  .fade-in {
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ---------- Thinking Dots Animation ---------- */
  @keyframes pulse-dot {
    0%, 80%, 100% { opacity: 0.4; transform: scale(1); }
    40% { opacity: 1; transform: scale(1.2); }
  }
`;

export default GlobalStyles;
